import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

import { AbstractDateInputComponent } from '@connect-services/client/components/input/date/abstract-date-input.component';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { CustomDateAdapterService } from '@connect-services/client/services/custom-date-adapter.service';
import { ADAPTER_DATE_FORMATS, TranslateService } from '@connect-services/client/services/translate.service';
import { InformationSchemaTableExtended } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum EntityTableDateCellContext {
    DETAIL = 'detail',
    TABLE = 'table',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    providers: [
        { deps: [TranslateService], provide: DateAdapter, useClass: CustomDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: ADAPTER_DATE_FORMATS },
    ],
    selector: 'app-entity-table-date-cell',
    standalone: true,
    styleUrl: './entity-table-date-cell.component.scss',
    templateUrl: './entity-table-date-cell.component.html',
})
export class EntityTableDateCellComponent extends AbstractDateInputComponent<unknown, InformationSchemaTableExtended> {
    public readonly context = input<EntityTableDateCellContext>(EntityTableDateCellContext.TABLE);

    protected readonly EntityTableDateCellContext = EntityTableDateCellContext;

    private readonly attributeValidationService = inject(AttributeValidationService);

    public constructor() {
        super();

        effect(() => {
            const baseControl = this.attributeValidationService.createControl(this.value(), this.info());

            this.control.setValidators(baseControl.validator);
            this.control.setAsyncValidators(baseControl.asyncValidator ?? null);
            this.control.setValue(baseControl.value, { emitEvent: false });
            this.control.updateValueAndValidity({ emitEvent: false });

            this.errorMessages.set(this.attributeValidationService.getErrorMessages(this.control));
        });
    }

    public handleBlur(): void {
        this.control.markAsTouched();
        this.control.updateValueAndValidity({ emitEvent: false });

        this.errorMessages.set(this.attributeValidationService.getErrorMessages(this.control));

        this.emitIfValid();
    }

    public onDateChange(event: MatDatepickerInputEvent<Date>): void {
        this.setValueAndErrors(event.value);
        this.emitIfValid();
    }

    private emitIfValid(): void {
        if (this.errorMessages().length) {
            return;
        }

        const raw = this.control.value;
        const isoValue = raw instanceof Date ? raw.toISOString() : raw;

        this.valueChange.emit(isoValue);
        this.onBlur.emit(isoValue);
    }

    private setValueAndErrors(value: Date | null): void {
        this.control.setValue(value);
        this.control.updateValueAndValidity({ emitEvent: false });
        this.errorMessages.set(this.attributeValidationService.getErrorMessages(this.control));
    }
}
